import { LoadedRegion } from "../AudioPlayer";
import { RegionInfo } from "../Track";
import { TrackInfo } from "../types";
import { StreamingAudioPlayer } from "../streaming/StreamingAudioPlayer";
import {
  PlaybackBackend,
  PlaybackBackendDebugState,
  PlaybackBackendLoadOptions,
  ReadyStateListener,
} from "./PlaybackBackend";

export class StreamingPlaybackBackend implements PlaybackBackend {
  readonly kind = "streaming" as const;

  private player = new StreamingAudioPlayer();

  load(options: PlaybackBackendLoadOptions): Promise<void> {
    return this.player.load(options);
  }

  get ready(): Promise<void> {
    return this.player.ready;
  }

  onReadyStateChange(listener: ReadyStateListener): () => void {
    return this.player.onReadyStateChange(listener);
  }

  play(timestamp: number): Promise<void> {
    return this.player.play(timestamp);
  }

  pause(): void {
    this.player.pause();
  }

  seek(timestamp: number): Promise<void> {
    return this.player.seek(timestamp);
  }

  setMasterGain(gain: number): void {
    this.player.setMasterGain(gain);
  }

  setPlaybackRate(rate: number): void {
    this.player.setPlaybackRate(rate);
  }

  setTranspose(semitones: number): void {
    this.player.setTranspose(semitones);
  }

  setTrackGain(trackId: string, gain: number): void {
    this.player.setTrackGain(trackId, gain);
  }

  addTracks(options: { tracks: TrackInfo[]; regions: LoadedRegion[] }): void {
    this.player.addTracks(options);
  }

  addEmptyTrack(id: string): void {
    this.player.addEmptyTrack(id);
  }

  removeTrack(id: string): void {
    this.player.removeTrack(id);
  }

  addRegionToTrack(trackId: string, region: LoadedRegion): void {
    this.player.addRegionToTrack(trackId, region);
  }

  removeRegionFromTrack(trackId: string, regionId: string): void {
    this.player.removeRegionFromTrack(trackId, regionId);
  }

  getRegionsInRange(trackId: string, start: number, end: number): RegionInfo[] {
    return this.player.getRegionsInRange(trackId, start, end);
  }

  getRegionBuffer(trackId: string, regionId: string): AudioBuffer | undefined {
    return this.player.getRegionBuffer(trackId, regionId);
  }

  getDebugState(): PlaybackBackendDebugState | null {
    return this.player.getDebugState();
  }

  destroy(): void {
    this.player.destroy();
  }
}
